import React, { useEffect, useRef, useState } from "react";
import {
  AvatarSquare,
  CrestChip,
  FlagChip,
  PositionPill,
  StatusPill,
  LabelChip,
  SourceLines,
  AttributionCaption,
  MenuPopover,
  MenuItem,
  Eyebrow,
  statusStyle,
  GLYPH,
} from "./v2";
import { radarShortPosition } from "../agent-radar/radarFormat";
import { radarLabelHex } from "../../constants/agentRadarChoices";
import { CLUB_STATUS_ORDER, clubStatusMeta } from "../../constants/clubRadarChoices";

// Club Radar entries table (desktop, frames 1a/1b — V2 rebuild, 1:1 from the
// artboard). Card r16; grid
// `minmax(280px,1.2fr) 140px 150px minmax(0,1fr) 200px 28px`;
// header row bg #F8FAFC 10.5/700 .07em; rows pad 11px 16px, divider #F1F5F9;
// footer caption 11.5 #9CA3AF. Columns: PLAYER · STATUS (pill + "by <person>")
// · LABELS · LATEST NOTE (quote + author·role) · SOURCE (permanent origin
// attribution, R2) · ⋯ row menu. Clicking a row opens the entry detail.
// Destructive row actions (move/remove) stay creator/ClubAdmin only (R6) —
// gated by `canManage`. Presentation only: same handlers as before.

// PLAYER is fluid, not a fixed 280px track (T013): with a hard 280px the
// column stayed 280px at every viewport — all surplus width went to LATEST
// NOTE — so "Mohamed Salah" ellipsised even at 2560px next to obvious
// whitespace. `minmax(280px,1.2fr)` keeps the artboard width as the FLOOR and
// lets the column grow with the container, mirroring how the request list
// sizes its identity column (`minmax(210px,1.1fr)` in
// src/components/agent-tender/AgentRequestListView.jsx:52). Names now
// ellipsise only when space genuinely runs out.
const COLS = "minmax(280px,1.2fr) 140px 150px minmax(0,1fr) 200px 28px";

// Person-first identity block (§4 item 10 — table cell variant, also reused by
// the 1e mobile rows via the `mobile` flag). Data comes from the entry payload;
// flags render ONLY via the platform image-flag idiom (Brief Rule 3) — when the
// API supplies no image URL, no flag renders (never emoji, never faked).
export const ClubPlayerCell = ({ player = {}, mobile = false, trailing }) => {
  const name = player.name || "Player";
  const clubName = player.club_name || player.club || "";
  const pos = radarShortPosition(player.position);
  const flagSrc = /^https?:\/\//.test(player.nationality_flag || "") ? player.nationality_flag : null;
  const line2 = [clubName, player.age].filter((x) => x !== null && x !== undefined && x !== "").join(` ${GLYPH.middot} `);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
      <AvatarSquare
        name={name}
        initials={player.initials}
        imageUrl={player.image_url}
        size={mobile ? 40 : 36}
        radius={mobile ? 11 : 10}
        fontSize={mobile ? 13 : 12}
      />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span
            style={{
              fontSize: mobile ? 14.5 : 14,
              fontWeight: 700,
              color: "#111827",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </span>
          {pos ? <PositionPill>{pos}</PositionPill> : null}
          <FlagChip src={flagSrc} size="row" />
          {trailing}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 3, minWidth: 0 }}>
          {clubName ? (
            <CrestChip
              name={clubName}
              logoUrl={player.club_crest_url}
              size={mobile ? 15 : 16}
              radius={4}
              fontSize={mobile ? 7 : 7.5}
            />
          ) : null}
          <span style={{ fontSize: 12, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {line2}
          </span>
        </div>
      </div>
    </div>
  );
};

const RowMenu = ({ entry, lists, canManage, onSetStatus, onCopy, onMove, onRemove }) => {
  const [open, setOpen] = useState(false);
  const [sub, setSub] = useState(null); // 'status' | 'copy' | 'move'
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSub(null);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const close = () => {
    setOpen(false);
    setSub(null);
  };

  return (
    <div ref={ref} style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Row actions"
        className="cursor-pointer"
        style={{ width: 28, height: 28, borderRadius: 8, border: "none", background: "none", color: "#9CA3AF", fontSize: 15, fontWeight: 700, fontFamily: "inherit" }}
      >
        {GLYPH.ellipsisMenu}
      </button>
      {open && (
        <MenuPopover
          width={200}
          style={{ position: "absolute", right: 0, top: 32, zIndex: 40, animation: "tp-pop 0.12s ease" }}
        >
          {sub === "status" && (
            <>
              <Eyebrow fontSize={10.5} spacing=".05em" style={{ padding: "6px 10px 2px" }}>Set status</Eyebrow>
              {CLUB_STATUS_ORDER.map((s) => (
                <MenuItem key={s} onClick={() => { close(); onSetStatus(entry, s); }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: statusStyle(s).fg, display: "inline-block" }} />
                    {clubStatusMeta(s).label}
                    {entry.status === s && <span style={{ color: "#16A34A", marginLeft: 4 }}>{GLYPH.check}</span>}
                  </span>
                </MenuItem>
              ))}
            </>
          )}
          {(sub === "copy" || sub === "move") && (
            <>
              <Eyebrow fontSize={10.5} spacing=".05em" style={{ padding: "6px 10px 2px" }}>
                {sub === "copy" ? "Copy to list" : "Move to list"}
              </Eyebrow>
              <div style={{ maxHeight: 200, overflowY: "auto" }}>
                {lists.filter((l) => l.id !== entry._listId).map((l) => (
                  <MenuItem key={l.id} onClick={() => { close(); (sub === "copy" ? onCopy : onMove)(entry, l); }}>{l.name}</MenuItem>
                ))}
              </div>
            </>
          )}
          {!sub && (
            <>
              <MenuItem onClick={() => setSub("status")}>Set status{GLYPH.ellipsis}</MenuItem>
              <MenuItem onClick={() => setSub("copy")}>Copy to list{GLYPH.ellipsis}</MenuItem>
              {canManage && <MenuItem onClick={() => setSub("move")}>Move to list{GLYPH.ellipsis}</MenuItem>}
              {canManage && (
                <MenuItem destructive onClick={() => { close(); onRemove(entry); }}>Remove from list</MenuItem>
              )}
            </>
          )}
        </MenuPopover>
      )}
    </div>
  );
};

// Label chip from the club's real palette: colour key → hex, rendered as the
// artboard's tint-bg/strong-fg pill (values are data-driven, never baked).
export const ClubLabelChip = ({ label, ...rest }) => {
  const hex = radarLabelHex(label.color);
  return (
    <LabelChip bg={`${hex}1A`} color={hex} {...rest}>{label.name}</LabelChip>
  );
};

const HeaderCell = ({ children }) => (
  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#9CA3AF" }}>
    {children}
  </div>
);

const ClubEntryRow = ({ entry, lists, canManage, onOpen, onSetStatus, onCopy, onMove, onRemove }) => {
  const origin = entry.origin || {};
  const note = entry.latest_note;
  return (
    <div
      onClick={() => onOpen(entry)}
      className="cursor-pointer"
      style={{ display: "grid", gridTemplateColumns: COLS, alignItems: "center", gap: 12, padding: "11px 16px", borderBottom: "1px solid #F1F5F9", background: "#FFFFFF" }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#FBFCFE")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "#FFFFFF")}
    >
      <ClubPlayerCell player={entry.player || entry} />
      <div style={{ minWidth: 0 }}>
        <StatusPill status={entry.status} label={clubStatusMeta(entry.status).label} />
        {entry.status_set_by_name && (
          <AttributionCaption style={{ marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            by {entry.status_set_by_name}
          </AttributionCaption>
        )}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, minWidth: 0 }}>
        {(entry.labels || []).slice(0, 3).map((l) => (
          <ClubLabelChip key={l.id} label={l} />
        ))}
      </div>
      <div style={{ minWidth: 0 }}>
        {note?.text ? (
          <>
            <div style={{ fontSize: 12.5, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {GLYPH.quoteOpen}{note.text}{GLYPH.quoteClose}
            </div>
            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {[note.author_name, note.author_role].filter(Boolean).join(` ${GLYPH.middot} `)}
            </div>
          </>
        ) : (
          <span style={{ fontSize: 12, color: "#C7D0DC" }}>No notes yet</span>
        )}
      </div>
      <SourceLines top={origin.top || "Added"} sub={origin.sub} />
      <RowMenu entry={entry} lists={lists} canManage={canManage} onSetStatus={onSetStatus} onCopy={onCopy} onMove={onMove} onRemove={onRemove} />
    </div>
  );
};

const ClubEntryTable = ({ entries, lists, canManage, selectedList, onOpen, onSetStatus, onCopy, onMove, onRemove, footer }) => (
  <div style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 16, overflow: "hidden" }}>
    <div style={{ overflowX: "auto" }}>
      <div style={{ minWidth: 980 }}>
        <div style={{ display: "grid", gridTemplateColumns: COLS, gap: 12, padding: "10px 16px", background: "#F8FAFC", borderBottom: "1px solid #E5E7EB" }}>
          <HeaderCell>Player</HeaderCell>
          <HeaderCell>Status</HeaderCell>
          <HeaderCell>Labels</HeaderCell>
          <HeaderCell>Latest note</HeaderCell>
          <HeaderCell>Source</HeaderCell>
          <HeaderCell />
        </div>
        {entries.map((entry) => (
          <ClubEntryRow
            key={entry.id}
            entry={{ ...entry, _listId: selectedList }}
            lists={lists}
            canManage={canManage}
            onOpen={onOpen}
            onSetStatus={onSetStatus}
            onCopy={onCopy}
            onMove={onMove}
            onRemove={onRemove}
          />
        ))}
        {footer && (
          <div style={{ padding: "10px 16px", fontSize: 11.5, color: "#9CA3AF" }}>{footer}</div>
        )}
      </div>
    </div>
  </div>
);

export default ClubEntryTable;
